import { SCROLL_AXES, type ScrollAxis } from "./scroll-box-axes"
import { intersectSpans, rectSpan, scrollportSpan, spanExtentPx, spansOverlap, type PixelSpan } from "./scroll-box-spans"
import { layoutRect } from "./scroll-box-styles"

const SIBLINGS_MEASURED_IN_FULL = 100
const VIEWS_MEASURED_BEYOND_EACH_EDGE = 2

export const MAXIMUM_ELEMENTS_READ_PER_WALK = 1400

export function isLongListParent(parent: Element): boolean {
  return parent.children.length > SIBLINGS_MEASURED_IN_FULL
}

export function nearViewSpan(box: Element, axis: ScrollAxis): PixelSpan {
  const view = scrollportSpan(box, axis)
  const marginPx = spanExtentPx(view) * VIEWS_MEASURED_BEYOND_EACH_EDGE
  return { startPx: view.startPx - marginPx, endPx: view.endPx + marginPx }
}

function placedRect(element: Element): DOMRect {
  const rect = element.getBoundingClientRect()
  return rect.width === 0 && rect.height === 0 ? layoutRect(element) : rect
}

export function childrenNearView(parent: Element, axis: ScrollAxis, nearView: PixelSpan): Element[] {
  const children = Array.from(parent.children)
  if (!isLongListParent(parent)) return children
  return children.filter((child) => spansOverlap(rectSpan(placedRect(child), axis), nearView))
}

export function extentNearViewPx(element: Element, axis: ScrollAxis, nearView: PixelSpan): number {
  return Math.max(0, spanExtentPx(intersectSpans(rectSpan(layoutRect(element), axis), nearView)))
}

export function descendantsNearView(box: Element): Element[] {
  const nearViews = SCROLL_AXES.map((axis) => ({ axis, span: nearViewSpan(box, axis) }))
  const isNearView = (element: Element) => {
    const rect = placedRect(element)
    return nearViews.every((nearView) => spansOverlap(rectSpan(rect, nearView.axis), nearView.span))
  }
  const descendants: Element[] = []
  const collect = (parent: Element) => {
    const children = Array.from(parent.children)
    for (const child of isLongListParent(parent) ? children.filter(isNearView) : children) {
      descendants.push(child)
      collect(child)
    }
  }
  collect(box)
  return descendants
}
